import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { API_BASE_URL, SKIP_AUTH } from './auth-config';
import { User } from './user';

export type LoginOutcome =
    | { status: 'authenticated' }
    | { status: 'mfa_required' };

interface Session {
    accessToken: string;
    refreshToken: string;
    /** Access-token absolute expiry (ms epoch). */
    expiresAtMs: number;
    /** When the session was authenticated (ms epoch) — drives the 1-hour cap. */
    authTimeMs: number;
    user: User;
}

/** What actually goes to storage: only the tokens, everything else is derived from them. */
interface StoredTokens {
    accessToken: string;
    refreshToken: string;
}

const STORAGE_KEY = 'ps_kc_session';
const REFRESH_SKEW_MS = 30_000;
/** A login is valid for one hour (mirrors the backend `assertFreshAuth`). */
export const MAX_SESSION_MS = 60 * 60 * 1000;

/**
 * Client-side Keycloak session. Talks only to our backend APIs (no client secret in the
 * browser); holds the access/refresh tokens, exposes the user, and attaches the bearer token.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakAuthClient {
    private readonly http: HttpClient = inject(HttpClient);
    private readonly base: string = inject(API_BASE_URL);
    private session: Session | null = this.restore();
    private refreshInFlight: Promise<string | null> | null = null;

    public currentUser(): User | null {
        return this.session?.user ?? null;
    }

    public authTimeMs(): number | null {
        return this.session?.authTimeMs ?? null;
    }

    public isSessionExpired(): boolean {
        return this.session ? Date.now() > this.session.authTimeMs + MAX_SESSION_MS : true;
    }

    public isAuthenticated(): boolean {
        return this.session !== null && !this.isSessionExpired();
    }

    /** Password sign-in. Returns `mfa_required` when a TOTP code is needed (call again with `otp`). */
    public async login(email: string, password: string, otp?: string): Promise<LoginOutcome> {
        const res = await this.postApi<{ status: string; accessToken?: string; refreshToken?: string; expiresIn?: number }>(
            '/auth/login',
            { email, password, otp },
            false,
        );
        if (res.status === 'mfa_required') {
            return { status: 'mfa_required' };
        }
        this.setSession(res.accessToken!, res.refreshToken!, res.expiresIn!);
        return { status: 'authenticated' };
    }

    public logout(): void {
        this.session = null;
        this.persist();
    }

    /** Returns a valid access token, refreshing if it is close to expiry; `null` when signed out. */
    public async getAccessToken(): Promise<string | null> {
        if (!this.session) {
            return null;
        }
        if (this.isSessionExpired()) {
            this.logout();
            return null;
        }
        if (Date.now() < this.session.expiresAtMs - REFRESH_SKEW_MS) {
            return this.session.accessToken;
        }
        return this.doRefresh();
    }

    /**
     * POSTs to a backend API path (e.g. `/auth/login`, `/intern/act`). `authInterceptor` adds
     * the bearer token and the timeout. `data` is sent as the raw JSON body — the Express
     * handlers read `req.body` directly, with no Firebase-callable `{ data }`/`{ result }`
     * envelope. Passing `null` (the intern `get-all` call) omits the body/Content-Type
     * entirely, since that handler rejects a non-empty body.
     */
    public async postApi<T>(path: string, data: unknown, attachAuth: boolean = true): Promise<T> {
        return await firstValueFrom(
            this.http.post<T>(`${ this.base }${ path }`, data, {
                context: new HttpContext().set(SKIP_AUTH, !attachAuth),
            }),
        );
    }

    private async doRefresh(): Promise<string | null> {
        if (!this.session) {
            return null;
        }
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        const refreshToken: string = this.session.refreshToken;
        this.refreshInFlight = (async (): Promise<string | null> => {
            try {
                const res = await this.postApi<{ accessToken: string; refreshToken: string; expiresIn: number }>(
                    '/auth/refresh',
                    { refreshToken },
                    false,
                );
                this.setSession(res.accessToken, res.refreshToken, res.expiresIn);
                return this.session!.accessToken;
            } catch {
                this.logout();
                return null;
            } finally {
                this.refreshInFlight = null;
            }
        })();
        return this.refreshInFlight;
    }

    private setSession(accessToken: string, refreshToken: string, expiresInSeconds: number): void {
        const claims: Record<string, unknown> = decodeJwt(accessToken);
        const nowSeconds: number = Date.now() / 1000;
        this.session = {
            accessToken,
            refreshToken,
            expiresAtMs: (numberClaim(claims['exp']) ?? nowSeconds + expiresInSeconds) * 1000,
            // The token was just issued, so "now" is a safe fallback for a missing claim.
            authTimeMs: (numberClaim(claims['auth_time']) ?? numberClaim(claims['iat']) ?? nowSeconds) * 1000,
            user: userFromClaims(claims),
        };
        this.persist();
    }

    /**
     * Rebuilds the session from the stored tokens. Storage is user-writable, so only the tokens
     * are read back and every other value comes from the access token's own claims — the backend
     * verifies its signature, so editing storage buys nothing but a rejected call.
     */
    private restore(): Session | null {
        try {
            const raw: string | null = localStorage.getItem(STORAGE_KEY);
            const stored: Partial<StoredTokens> | null = raw ? JSON.parse(raw) as Partial<StoredTokens> : null;
            if (typeof stored?.accessToken !== 'string' || typeof stored.refreshToken !== 'string') {
                return null;
            }
            const claims: Record<string, unknown> = decodeJwt(stored.accessToken);
            const expiresAtSeconds: number | null = numberClaim(claims['exp']);
            const authTimeSeconds: number | null = numberClaim(claims['auth_time']) ?? numberClaim(claims['iat']);
            // Without those claims the session cannot be time-bound, so it does not count.
            if (expiresAtSeconds === null || authTimeSeconds === null) {
                return null;
            }
            return {
                accessToken: stored.accessToken,
                refreshToken: stored.refreshToken,
                expiresAtMs: expiresAtSeconds * 1000,
                authTimeMs: authTimeSeconds * 1000,
                user: userFromClaims(claims),
            };
        } catch {
            return null;
        }
    }

    private persist(): void {
        try {
            if (this.session) {
                const tokens: StoredTokens = {
                    accessToken: this.session.accessToken,
                    refreshToken: this.session.refreshToken,
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            // ignore storage failures (private mode etc.)
        }
    }
}

function decodeJwt(token: string): Record<string, unknown> {
    try {
        const payload: string = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(payload)) as Record<string, unknown>;
    } catch {
        return {};
    }
}

function userFromClaims(claims: Record<string, unknown>): User {
    return {
        id: stringClaim(claims['sub']),
        email: stringClaim(claims['email']),
        name: stringClaim(claims['name']) || stringClaim(claims['preferred_username']),
    };
}

function stringClaim(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function numberClaim(value: unknown): number | null {
    return typeof value === 'number' ? value : null;
}
